import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Star, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * RatingsWidget — reusable star picker + optional review textarea.
 *
 * Props:
 *   contentType: one of course|ebook|live_class|audio_room|exam|past_paper|reel|note|booking|teacher
 *   contentId:   id string (for 'teacher' this is the teacher_id)
 *   compact:     bool — render only the stars (no review box, no submit button)
 *   onSubmitted: () => void — optional callback after submit
 *   title:       optional heading text (default: 'Rate this {contentType}')
 */
export default function RatingsWidget({ contentType, contentId, compact = false, onSubmitted, title }) {
  const [my, setMy] = useState(null);
  const [hover, setHover] = useState(0);
  const [value, setValue] = useState(0);
  const [review, setReview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const endpoint = contentType === 'teacher'
    ? `${API}/ratings/teacher/${contentId}`
    : `${API}/ratings/${contentType}/${contentId}`;
  const myEndpoint = `${API}/ratings/my/${contentType}/${contentId}`;

  useEffect(() => {
    if (!contentType || !contentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) { setLoading(false); return; }
        const res = await axios.get(myEndpoint, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled && res.data?.rating) {
          setMy(res.data);
          setValue(res.data.rating || 0);
          setReview(res.data.review || '');
        }
      } catch (e) { /* not fatal */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [contentType, contentId, myEndpoint]);

  const submit = async (nextValue) => {
    const val = nextValue || value;
    if (!val) { toast.error('Pick a star rating first'); return; }
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please sign in to rate'); return; }
    setSaving(true);
    try {
      await axios.post(endpoint, { rating: val, review: review || null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(my ? 'Rating updated' : 'Thanks for rating!');
      setMy({ rating: val, review });
      if (onSubmitted) onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit rating');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="rating-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading your rating…
      </div>
    );
  }

  return (
    <div className={`bg-slate-800/40 border border-slate-700 rounded-xl p-4 ${compact ? '' : 'space-y-3'}`} data-testid={`ratings-widget-${contentType}`}>
      {!compact && (
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-white">
            {title || (my ? 'Your rating' : `Rate this ${String(contentType).replace('_', ' ')}`)}
          </h4>
          {my && <span className="text-[10px] text-slate-500">You already rated — updating will replace your previous rating.</span>}
        </div>
      )}

      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((s) => {
          const active = (hover || value) >= s;
          return (
            <button
              key={s}
              type="button"
              onMouseEnter={() => setHover(s)}
              onClick={() => {
                setValue(s);
                if (compact) submit(s);
              }}
              className="p-1 rounded hover:scale-110 transition-transform"
              data-testid={`star-${s}`}
              disabled={saving}
            >
              <Star
                className={`${compact ? 'w-4 h-4' : 'w-6 h-6'} transition-colors ${active ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`}
              />
            </button>
          );
        })}
        {compact && value > 0 && (
          <span className="text-[11px] text-slate-400 ml-1">{value}/5</span>
        )}
      </div>

      {!compact && (
        <>
          <textarea
            value={review}
            onChange={(e) => setReview(e.target.value)}
            placeholder="Add an optional review (what you liked, what could improve)…"
            rows={3}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="review-textarea"
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => submit()}
              disabled={saving || value === 0}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
              data-testid="rating-submit-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {my ? 'Update rating' : 'Submit rating'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
